import {
    useState,
    useRef,
    useCallback,
    MouseEvent,
} from "react";
import { CertificationFormProps, FormErrors, Mode } from "../../types/certificate";
import { EMPTY_FORM, MOCK_CERT } from "../../constants/certificate";
import { hydrateForm } from "../../helpers/certificate";
import { FormState } from "../../components/pages/certificates/types";
import Tag from "../../components/common/UI/Tag";
import Section from "../../components/common/UI/Section";
import Field from "../../components/common/Form/Field";
import Input from "../../components/ui/Input";
import Textarea from "../../components/common/Form/Textarea";
import { ImageUpload } from "../../components/common/Media/Image";
import CertPreviewPanel from "../../components/pages/certificates/CertPreviewPanel";
import QuickInfoPanel from "../../components/pages/certificates/QuickInfoPanel";


export default function ManageCertificates({
    mode: initialMode = "add",
    certification: initialCert,
}: CertificationFormProps) {
    const [mode, setMode] = useState<Mode>(initialMode);
    const [form, setForm] = useState<FormState>(() =>
        initialMode === "edit" && initialCert ? hydrateForm(initialCert) : EMPTY_FORM()
    );
    const [errors, setErrors] = useState<FormErrors>({});
    const [saved, setSaved] = useState(false);
    const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const set = <K extends keyof FormState>(key: K) => (val: FormState[K]) =>
        setForm((f) => ({ ...f, [key]: val }));

    const clearErr = (k: keyof FormErrors) => setErrors((e) => ({ ...e, [k]: null }));

    const validate = (): boolean => {
        const e: FormErrors = {};
        if (!form.title.trim()) e.title = "Certification title is required.";
        if (!form.description.trim()) e.description = "Description is required.";
        if (!form.issuedBy.trim()) e.issuedBy = "Issuing organisation is required.";
        if (!form.issuedDate) e.issuedDate = "Issue date is required.";
        if (!form.imagePreview) e.image = "Please upload a certification image.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        setSaved(true);
        saveTimer.current = setTimeout(() => setSaved(false), 2800);
    };

    const handleReset = () => { setForm(EMPTY_FORM()); setErrors({}); setMode("add"); };
    const handleLoadDemo = () => { setForm(hydrateForm(MOCK_CERT)); setMode("edit"); setErrors({}); };

    const handleImageFile = useCallback((file: File) => {
        const url = URL.createObjectURL(file);
        setForm((f) => ({ ...f, imageFile: file, imagePreview: url }));
        clearErr("image");
    }, []);

    const handleImageClear = () => setForm((f) => ({ ...f, imageFile: null, imagePreview: "", imageUrl: "" }));

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        // :root {
        //   --or:#BF3D0C; --or2:#E8593C; --or-dim:rgba(191,61,12,0.10);
        //   --text:#e8e6de; --text2:#b4b2a9; --text3:#6a6a65;
        //   --border:rgba(255,255,255,0.08); --card:rgba(255,255,255,0.025);
        //   --input-bg:rgba(255,255,255,0.035); --section-header:rgba(255,255,255,0.02);
        // //   --surface:#111110; 
        //   --red:#e05252;
        // }
        body { margin:0; font-family:'DM Sans',sans-serif; }
        input::placeholder, textarea::placeholder { color:var(--text3); }
        input, textarea, select { color:var(--text); }
        ::-webkit-scrollbar { width:5px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:var(--border); border-radius:4px; }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes toastIn     { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
        .cf-layout { display:grid; grid-template-columns:1fr 288px; gap:24px; align-items:start; }
        .cf-sidebar { display:flex; flex-direction:column; gap:14px; position:sticky; top:24px; }
        @media(max-width:1080px){.cf-layout{grid-template-columns:1fr;}.cf-sidebar{position:static;}}
        @media(max-width:480px){.manage-cert-header{display:block !important;}}
      `}</style>

            {/* Toast */}
            {saved && (
                <div style={{
                    position: "fixed", top: 20, right: 20, zIndex: 999,
                    background: "#1a2e1a", border: "1px solid rgba(90,200,90,0.25)",
                    borderRadius: 10, padding: "12px 18px", display: "flex", alignItems: "center", gap: 10,
                    animation: "toastIn 0.25s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                }}>
                    <div style={{
                        width: 20, height: 20, borderRadius: "50%", background: "rgba(90,200,90,0.2)",
                        border: "1px solid rgba(90,200,90,0.4)", display: "flex", alignItems: "center",
                        justifyContent: "center", fontSize: 12, color: "#6ddc6d",
                    }}>✓</div>
                    <span style={{ fontSize: 14, color: "#9be09b" }}>
                        Certification {mode === "edit" ? "updated" : "saved"} successfully
                    </span>
                </div>
            )}

            <div style={{ minHeight: "100vh", padding: "32px 28px" }}>
                <div style={{ maxWidth: 1200, margin: "0 auto" }}>

                    {/* Header */}
                    <div style={{
                        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                        marginBottom: 24, animation: "fadeSlideIn 0.3s ease",
                    }}
                        className="manage-cert-header"
                    >
                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                                <Tag>{mode === "edit" ? "Editing" : "New Certification"}</Tag>
                            </div>
                            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "var(--text)", letterSpacing: "-0.4px" }}>
                                {mode === "edit" ? (form.title || "Edit Certification") : "Add Certification"}
                            </h1>
                            <p style={{ margin: "4px 0 0", fontSize: 14, color: "var(--text3)" }}>
                                {mode === "edit" ? "Update the certification details below." : "Fill in the details to create a new certification."}
                            </p>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, marginTop: 10 }}>
                            {/* <button onClick={handleReset} style={{
                                fontSize: 13, padding: "8px 14px", borderRadius: 8,
                                border: "1px solid var(--border)", background: "transparent",
                                color: "var(--text2)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
                            }}>+ New</button> */}
                            {/* <button onClick={handleLoadDemo} style={{
                                fontSize: 13, padding: "8px 14px", borderRadius: 8,
                                border: "1px solid rgba(191,61,12,0.3)", background: "var(--or-dim)",
                                color: "var(--or)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
                            }}>Load Demo</button> */}
                        </div>
                    </div>

                    {/* Two-column layout */}
                    <div className="cf-layout">

                        {/* ── Left: form ── */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 16, animation: "fadeSlideIn 0.35s ease 0.05s both", minWidth: 0 }}>

                            {/* Core details */}
                            <Section title="Certification Details" tag="Required">
                                <Field label="Certification Title" required error={errors.title}>
                                    <Input
                                        value={form.title}
                                        onChange={(e) => { set("title")(e.target.value); clearErr("title"); }}
                                        placeholder="e.g. ISO 9001:2015 Quality Management"
                                        maxLength={100}
                                    />
                                </Field>
                                <Field label="Description" required hint="Displayed on the public certification page" error={errors.description}>
                                    <Textarea
                                        value={form.description}
                                        onChange={(e) => { set("description")(e.target.value); clearErr("description"); }}
                                        placeholder="Describe what this certification represents and its significance…"
                                        rows={5} maxLength={800}
                                    />
                                </Field>
                            </Section>

                            {/* Image upload */}
                            <Section title="Certification Image" tag="Certificate / Badge">
                                <div style={{
                                    background: "rgba(191,61,12,0.06)", border: "1px solid rgba(191,61,12,0.15)",
                                    borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "var(--text3)", lineHeight: 1.6,
                                }}>
                                    💡 Upload a high-resolution scan or badge image of the certificate. Recommended minimum 800 × 600 px.
                                </div>
                                <ImageUpload
                                    preview={form.imagePreview}
                                    error={errors.image}
                                    onFile={handleImageFile}
                                    onClear={handleImageClear}
                                />
                            </Section>

                            {/* Issuance details */}
                            <Section title="Issuance Details">
                                <Field label="Issuing Organisation" required error={errors.issuedBy}>
                                    <Input
                                        value={form.issuedBy}
                                        onChange={(e) => { set("issuedBy")(e.target.value); clearErr("issuedBy"); }}
                                        placeholder="e.g. Bureau Veritas Certification"
                                        maxLength={120}
                                    />
                                </Field>

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                                    <Field label="Issue Date" required error={errors.issuedDate}>
                                        <Input
                                            type="date" value={form.issuedDate}
                                            onChange={(e) => { set("issuedDate")(e.target.value); clearErr("issuedDate"); }}
                                        />
                                    </Field>
                                    <Field label="Expiry Date" hint="Leave blank if no expiry">
                                        <Input
                                            type="date" value={form.expiryDate}
                                            onChange={(e) => set("expiryDate")(e.target.value)}
                                        />
                                    </Field>
                                </div>

                                <Field label="Status">
                                    <div style={{ display: "flex", gap: 8 }}>
                                        {(["active", "pending", "expired"] as const).map((s) => {
                                            const colorMap = { active: "#6ddc6d", pending: "#f5a623", expired: "#e05252" };
                                            const isActive = form.status === s;
                                            return (
                                                <button key={s} onClick={() => set("status")(s)} style={{
                                                    fontSize: 12, padding: "7px 14px", borderRadius: 8, cursor: "pointer",
                                                    fontFamily: "'DM Sans',sans-serif", textTransform: "capitalize",
                                                    border: `1px solid ${isActive ? `${colorMap[s]}44` : "var(--border)"}`,
                                                    background: isActive ? `${colorMap[s]}14` : "transparent",
                                                    color: isActive ? colorMap[s] : "var(--text3)",
                                                    fontWeight: isActive ? 600 : 400,
                                                    transition: "all 0.15s",
                                                }}>{s}</button>
                                            );
                                        })}
                                    </div>
                                </Field>
                            </Section>

                            {/* Footer actions */}
                            <div style={{
                                display: "flex", justifyContent: "flex-end", gap: 10,
                                paddingTop: 20, borderTop: "1px solid var(--border)",
                            }}>
                                <button onClick={handleReset} style={{
                                    padding: "10px 20px", borderRadius: 9, border: "1px solid var(--border)",
                                    background: "transparent", color: "var(--text2)", fontSize: 14, cursor: "pointer",
                                    fontFamily: "'DM Sans',sans-serif", transition: "all 0.15s",
                                }}
                                    onMouseEnter={(e: MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                                    onMouseLeave={(e: MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = "transparent"; }}
                                >Discard</button>
                                <button onClick={handleSave} style={{
                                    padding: "10px 24px", borderRadius: 9,
                                    border: "1px solid rgba(191,61,12,0.35)",
                                    background: "linear-gradient(135deg,#BF3D0C,#E8593C)",
                                    color: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer",
                                    fontFamily: "'DM Sans',sans-serif",
                                    boxShadow: "0 2px 12px rgba(191,61,12,0.25)",
                                    transition: "opacity 0.15s, transform 0.15s",
                                }}
                                    onMouseEnter={(e: MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.opacity = "0.88"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                                    onMouseLeave={(e: MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}
                                >
                                    {mode === "edit" ? "Save Changes" : "Publish Certification"}
                                </button>
                            </div>
                        </div>

                        {/* ── Right: sticky sidebar ── */}
                        <aside className="cf-sidebar" style={{ animation: "fadeSlideIn 0.4s ease 0.12s both" }}>
                            <CertPreviewPanel form={form} />
                            <QuickInfoPanel form={form} />
                        </aside>

                    </div>
                </div>
            </div>
        </>
    );
}